/**
 * TempoSupply – LPO Mengenrabatt Checkout
 * Greift nur ein, wenn der Bestellen-Button ([data-live-checkout]) geklickt wird
 * und der Warenkorb LPO-Mengenrabatt-Positionen enthält.
 */
(function () {
  'use strict';

  var PROP_ID = '_cl_options_quantity_id';
  var PROP_DISCOUNT = '_cl_options_quantity_discount';
  var CHECKOUT_TIMEOUT_MS = 5000;
  var busy = false;

  function getConfig() {
    var cfg =
      window.Cloudlift && window.Cloudlift.options && window.Cloudlift.options.config
        ? window.Cloudlift.options.config
        : {};

    return {
      shop: cfg.shop || (window.Shopify && window.Shopify.shop) || '',
      api: cfg.api || 'https://api.cloudlift.app',
      checkoutKey: cfg.checkoutConfig && cfg.checkoutConfig.key
    };
  }

  function cartHasQuantityItems(cart) {
    if (!cart || !cart.items) return false;

    return cart.items.some(function (item) {
      var props = item.properties || {};
      return props[PROP_ID] && props[PROP_DISCOUNT];
    });
  }

  function fetchCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      return response.json();
    });
  }

  function parseResponseBody(response) {
    if (!response) return Promise.resolve(null);

    return response
      .text()
      .then(function (text) {
        if (!text) return null;
        try {
          return JSON.parse(text);
        } catch (error) {
          return null;
        }
      })
      .catch(function () {
        return null;
      });
  }

  function syncCartWithCloudlift(cart, config) {
    if (!config.shop) return Promise.resolve(null);

    var url = config.api + '/api/checkout/cart?shop=' + encodeURIComponent(config.shop);

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(cart),
      credentials: 'omit',
      keepalive: true
    }).then(parseResponseBody);
  }

  function buildDiscountPayload(cart, config) {
    return {
      items: cart.items.map(function (item) {
        return {
          id: item.id,
          quantity: item.quantity,
          properties: item.properties || {},
          productId: item.product_id
        };
      }),
      discount: { type: 0, code: '' },
      currency: cart.currency,
      country: window.Shopify && window.Shopify.country,
      key: config.checkoutKey || undefined
    };
  }

  function requestCheckoutDiscount(cart, config) {
    if (!config.shop) return Promise.resolve(null);

    var url = config.api + '/api/checkout/discount?shop=' + encodeURIComponent(config.shop);

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ checkout: buildDiscountPayload(cart, config) }),
      credentials: 'omit'
    }).then(parseResponseBody);
  }

  function wait(ms) {
    return Promise.resolve().then(function () {
      return new Promise(function (resolve) {
        setTimeout(resolve, ms);
      });
    });
  }

  function withTimeout(promise, ms) {
    return Promise.race([
      promise,
      wait(ms).then(function () {
        return null;
      })
    ]);
  }

  function extractDiscountCode(result, cart) {
    var payload = result && (result.data || result);
    if (payload && payload.code) return payload.code;

    if (cart && cart.items) {
      for (var i = 0; i < cart.items.length; i++) {
        var item = cart.items[i];
        if (item.discounts && item.discounts.length && item.discounts[0].title) {
          return item.discounts[0].title;
        }
      }
    }

    return null;
  }

  function saveDiscountCode(code) {
    document.cookie = 'discount_code=' + encodeURIComponent(code) + '; path=/; SameSite=Lax';
    try {
      localStorage.setItem('cl_discount', JSON.stringify({ code: code, valid: true, type: 1 }));
    } catch (error) {}
  }

  function getCheckoutTrigger(event) {
    var submitter = event.submitter;
    if (!submitter) return null;
    if (submitter.matches('[data-live-checkout]')) return submitter;
    return submitter.closest('[data-live-checkout]');
  }

  function getCheckoutForm(trigger) {
    if (!trigger || !trigger.closest) return null;
    return trigger.closest('form[action*="/cart"]');
  }

  function setFormProcessing(form, on) {
    if (!form) return;
    if (on) {
      form.classList.remove('processed');
      form.classList.add('processing');
    } else {
      form.classList.remove('processing');
    }
  }

  function goToCheckout() {
    window.location.assign('/checkout');
  }

  function goToCheckoutWithCode(code) {
    saveDiscountCode(code);
    window.location.assign(
      '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent('/checkout')
    );
  }

  function resolveDiscountCode(cart, config) {
    return withTimeout(
      syncCartWithCloudlift(cart, config).then(function (syncResult) {
        var code = extractDiscountCode(syncResult, cart);
        if (code) return code;

        return wait(500)
          .then(fetchCart)
          .then(function (freshCart) {
            code = extractDiscountCode(null, freshCart);
            if (code) return code;

            return requestCheckoutDiscount(freshCart, config).then(function (discountResult) {
              return extractDiscountCode(discountResult, freshCart);
            });
          });
      }),
      CHECKOUT_TIMEOUT_MS
    );
  }

  function prepareCheckout(form) {
    if (busy) return;
    busy = true;
    setFormProcessing(form, true);

    var config = getConfig();

    fetchCart()
      .then(function (cart) {
        if (!cart.items || !cart.items.length || !cartHasQuantityItems(cart)) {
          busy = false;
          goToCheckout();
          return;
        }

        return resolveDiscountCode(cart, config).then(function (code) {
          busy = false;
          if (code) {
            goToCheckoutWithCode(code);
          } else {
            goToCheckout();
          }
        });
      })
      .catch(function () {
        busy = false;
        goToCheckout();
      });
  }

  function handleCheckout(event) {
    var form = event.target;
    var trigger;

    if (!form || form.tagName !== 'FORM') return;
    if (!form.getAttribute('action') || form.getAttribute('action').indexOf('/cart') === -1) return;

    trigger = getCheckoutTrigger(event);
    if (!trigger) return;

    if (!getCheckoutForm(trigger)) return;

    event.preventDefault();

    prepareCheckout(form);
  }

  document.addEventListener('submit', handleCheckout, true);
})();
